// The editor's "Linked to" field (decision 90 v2): a client, a project and/or a task, each searchable
// and removable. The server checks every link (lib/noteLinks.js): a record the person cannot see, or
// one from the other workspace, is refused, and the refusal is said beside Save like any save failure.
import React, { useMemo, useRef, useState } from 'react';
import { Popover, SearchInput, Skeleton, Banner, Button } from '../../ui';
import './notesBrowse.css';

export const LINK_KINDS = [
  { kind: 'contact', field: 'contactId', icon: '👤', group: 'Clients', noun: 'client' },
  { kind: 'project', field: 'projectId', icon: '📁', group: 'Projects', noun: 'project' },
  { kind: 'task', field: 'taskId', icon: '✅', group: 'Tasks', noun: 'task' },
];

// options: { contacts, projects, tasks } as [{ id, label }], status 'loading' | 'ready' | 'failed', error
export default function LinkedTo({ note, options, onChange, onRetry, labelFor, personal = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const anchor = useRef(null);
  const set = LINK_KINDS.filter((k) => note[k.field] !== null && note[k.field] !== undefined && note[k.field] !== '');

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    return LINK_KINDS.map((k) => {
      const all = options[`${k.kind}s`] || [];
      const hits = all.filter((o) => !query || o.label.toLowerCase().includes(query)).slice(0, 8);
      return { ...k, hits, group: k.kind === 'contact' && personal ? 'People' : k.group, noun: k.kind === 'contact' && personal ? 'person' : k.noun };
    }).filter((g) => g.hits.length);
  }, [q, options, personal]);

  const choose = (k, o) => {
    onChange({ [k.field]: k.kind === 'project' ? Number(o.id) : String(o.id) });
    setOpen(false);
    setQ('');
  };

  return (
    <div className="nt-links" role="group" aria-label="Linked to">
      <span className="nt-links__label">Linked to</span>
      {set.map((k) => {
        const label = labelFor(k.kind, note[k.field]);
        return (
          <span key={k.kind} className={`nt-pill nt-pill--${k.kind}`}>
            <span aria-hidden="true">{k.icon} </span>{label}
            <button type="button" className="nt-pill__x ui-hit" aria-label={`Remove the link to ${label}`} onClick={() => onChange({ [k.field]: null })}>✕</button>
          </span>
        );
      })}
      <button ref={anchor} type="button" className="nt-links__add" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {set.length ? '+ Link another' : `+ Link to a ${personal ? 'person' : 'client'}, project or task`}
      </button>
      <Popover anchorRef={anchor} open={open} onClose={() => setOpen(false)} title="Link this note" align="left" className="nt-links__pop">
        <div className="nt-links__body">
          <SearchInput label="Search clients, projects and tasks" placeholder="Search clients, projects, tasks…" value={q} autoFocus
            onChange={(e) => setQ(e.target.value)} />
          {options.status === 'loading' ? (
            <Skeleton lines={3} label="Loading what you can link to" />
          ) : options.status === 'failed' ? (
            <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={onRetry}>Try again</Button>}>{options.error}</Banner>
          ) : results.length === 0 ? (
            <p className="nt-links__none">{q.trim() ? `Nothing called "${q.trim()}".` : 'No clients, projects or tasks yet.'}</p>
          ) : results.map((g) => (
            <div key={g.kind} className="nt-links__group">
              <span className="nt-links__group-title">{g.group}</span>
              {g.hits.map((o) => (
                <button key={o.id} type="button" className="nt-links__opt" onClick={() => choose(g, o)}
                  aria-label={`Link to the ${g.noun} ${o.label}`}>
                  <span aria-hidden="true">{g.icon}</span> {o.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
}
